import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface Team {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
}

interface TeamsResponse {
  teams: Team[];
}

export function Teams() {
  const { organization } = useOrganization();
  const { get, post, delete: deleteApi } = useApi();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create team modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  async function fetchTeams() {
    try {
      setLoading(true);
      setError(null);
      const response = await get<TeamsResponse>('/teams');
      setTeams(response.teams);
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      setCreating(true);
      const team = await post<Team>('/teams', {
        name: newTeamName.trim(),
        description: newTeamDescription.trim() || null,
      });
      setTeams([team, ...teams]);
      setShowCreateModal(false);
      setNewTeamName('');
      setNewTeamDescription('');
    } catch (err: any) {
      alert(err.message || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteTeam(teamId: string, teamName: string) {
    if (!confirm(`Are you sure you want to delete "${teamName}"? This will remove all members from the team.`)) {
      return;
    }

    try {
      await deleteApi(`/settings/teams/${teamId}`);
      setTeams(teams.filter(t => t.id !== teamId));
    } catch (err: any) {
      alert(err.message || 'Failed to delete team');
    }
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <UsersIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
        <h2 className="text-xl font-medium text-white mb-2">Organization Required</h2>
        <p className="text-slate-400">
          Teams are only available within organizations. Create or join an organization to manage teams.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/settings" className="text-slate-400 hover:text-white">Settings</Link>
        <span className="mx-2 text-slate-600">/</span>
        <span className="text-white">Teams</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Teams</h1>
          <p className="mt-1 text-slate-400">Manage teams in {organization.name}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Team
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Teams list */}
      {teams.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-lg border border-slate-700">
          <UsersIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No teams yet</h3>
          <p className="text-slate-400 mb-4">Create your first team to organize members.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Team
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="bg-slate-800 rounded-lg border border-slate-700 p-5 hover:border-slate-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <Link to={`/settings/teams/${team.id}`} className="flex-1">
                  <h3 className="text-lg font-medium text-white hover:text-green-400">
                    {team.name}
                  </h3>
                  {team.description && (
                    <p className="mt-1 text-sm text-slate-400 line-clamp-2">
                      {team.description}
                    </p>
                  )}
                </Link>
                <button
                  onClick={() => handleDeleteTeam(team.id, team.name)}
                  className="ml-2 p-1 text-slate-500 hover:text-red-400 rounded"
                  title="Delete team"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-4 flex items-center text-sm text-slate-400">
                <UsersIcon className="w-4 h-4 mr-1" />
                {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
              </div>
              <Link
                to={`/settings/teams/${team.id}`}
                className="mt-3 inline-flex items-center text-sm text-green-500 hover:text-green-400"
              >
                Manage team
                <ChevronRightIcon className="w-4 h-4 ml-1" />
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Create Team</h2>
            <form onSubmit={handleCreateTeam}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="teamName" className="block text-sm font-medium text-slate-300 mb-1">
                    Team Name *
                  </label>
                  <input
                    id="teamName"
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Sales East"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="teamDescription" className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="teamDescription"
                    value={newTeamDescription}
                    onChange={(e) => setNewTeamDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newTeamName.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
